import { useEffect, useState } from "react";
import { api } from "../api";

const originals = new WeakMap<Text, string>();
const cache = new Map<string, Record<string, string>>();

function eligible(node: Text) {
  const parent = node.parentElement;
  const value = node.nodeValue || "";
  return Boolean(parent && value.trim() && !parent.closest("[data-no-translate],script,style,textarea,code,pre") && !/^[-+0-9:/.·%]+$/.test(value.trim()));
}

export function useLoginTranslation(eventId: number, language: string) {
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const root = document.querySelector(".login-page");
    if (!root || !eventId || !language) return;
    let cancelled = false;
    let timer = 0;
    let running = false;

    async function apply() {
      if (cancelled || running) return;
      running = true;
      setLoading(true);
      try {
        const key = `${eventId}:${language.toLowerCase()}`;
        let translations: Record<string, string> = {};
        if (!/^es(?:-|$)/i.test(language)) {
          translations = cache.get(key) || {};
          if (!cache.has(key)) {
            const result = await api<{ translations: Record<string, string> }>(`/api/public/translations?eventId=${eventId}&language=${encodeURIComponent(language)}`);
            translations = result.translations || {};
            cache.set(key, translations);
          }
        }
        if (cancelled) return;
        const walker = document.createTreeWalker(root!, NodeFilter.SHOW_TEXT);
        let current = walker.nextNode();
        while (current) {
          const node = current as Text;
          if (eligible(node)) {
            if (!originals.has(node)) originals.set(node, node.nodeValue || "");
            const original = originals.get(node) || "";
            const source = original.trim();
            const translated = translations[source] || source;
            const next = original.replace(source, translated);
            if (node.nodeValue !== next) node.nodeValue = next;
          }
          current = walker.nextNode();
        }
        document.documentElement.lang = language;
        window.localStorage.setItem(`organize-login-language-${eventId}`, language);
      } finally {
        running = false;
        if (!cancelled) setLoading(false);
      }
    }

    const schedule = () => { window.clearTimeout(timer); timer = window.setTimeout(() => { void apply(); }, 80); };
    const observer = new MutationObserver(schedule);
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    void apply();
    return () => { cancelled = true; window.clearTimeout(timer); observer.disconnect(); };
  }, [eventId, language]);
  return { loading };
}
